export function getResponseRequestId(response, payload = {}) {
    return (
        payload?.requestId ||
        payload?.error?.requestId ||
        response?.headers?.get?.("x-request-id") ||
        response?.headers?.get?.("x-correlation-id") ||
        null
    );
}

const OTP_ERROR_MESSAGES = Object.freeze({
    AUTH_OTP_EXPIRED: "This verification code has expired. Request a new code and try again.",
    AUTH_OTP_INVALID: "That verification code was not accepted. Check the code and try again, or request a new one.",
    AUTH_OTP_RATE_LIMITED: "Please wait a moment before requesting another code.",
    AUTH_OTP_LOCKED: "Too many incorrect verification attempts were made. Request a new code and try again.",
    AUTH_OTP_CHALLENGE_NOT_FOUND: "This verification attempt could not be found. Request a new code and try again.",
    AUTH_OTP_CHALLENGE_CLOSED: "This verification attempt is no longer active. Request a new code and try again.",
    AUTH_OTP_CHANNEL_MISMATCH: "This verification code was issued for a different sign-in method. Request a new code and try again.",
});

const SIGN_IN_ERROR_MESSAGES = Object.freeze({
    AUTH_OTP_RATE_LIMITED: "Please wait a moment before requesting another code.",
    AUTH_EMAIL_INVALID: "Enter a valid email address.",
    AUTH_SMS_DESTINATION_INVALID: "Enter a valid mobile number, for example 04xx xxx xxx.",
    AUTH_OTP_DESTINATION_REQUIRED: "Enter the sign-in email address or mobile number linked to your account.",
    AUTH_USER_NOT_FOUND: "User not found",
    AUTH_PORTAL_ACCESS_DENIED: "You do not have access to the requested portal.",
    AUTH_REFERRER_AGREEMENT_NOT_ACCEPTED: "You have not accepted the referral agreement.",
});

function getRetryAfterSeconds(error) {
    const value =
        error?.retryAfterSeconds ||
        error?.details?.retryAfterSeconds ||
        error?.details?.retryAfter ||
        error?.details?.retry_after;
    const seconds = Number(value);

    return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
}

function formatRetryAfterDuration(seconds) {
    if (seconds < 60) {
        return "less than 1 minute";
    }

    const minutes = Math.ceil(seconds / 60);

    if (minutes < 60) {
        return `${minutes} minute${minutes === 1 ? "" : "s"}`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const hourLabel = `${hours} hour${hours === 1 ? "" : "s"}`;

    if (!remainingMinutes) {
        return hourLabel;
    }

    return `${hourLabel} ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}`;
}

function getRateLimitMessage(error) {
    const retryAfterSeconds = getRetryAfterSeconds(error);

    if (!retryAfterSeconds) {
        return "Please wait before requesting another code.";
    }

    return `Please wait before requesting another code. You can request another code in about ${formatRetryAfterDuration(retryAfterSeconds)}.`;
}

export function getAuthErrorCode(error) {
    const code = String(error?.code || "").trim();
    return code || null;
}

export function getAuthRequestId(error) {
    const requestId = String(error?.requestId || "").trim();
    return requestId || null;
}

function getCodeMessage(error, catalog) {
    const code = getAuthErrorCode(error);

    return code ? catalog[code] || null : null;
}

export function isNetworkError(error) {
    const browserIsOffline = typeof navigator !== "undefined" && navigator.onLine === false;
    return browserIsOffline || /network|network changed|err_network_changed|failed to fetch|load failed|abort|timeout|timed out/i.test(`${error?.name || ""} ${error?.message || ""}`);
}

export function getOtpErrorMessage(error) {
    const message = String(error?.message || "").toLowerCase();

    if (isNetworkError(error)) {
        return "We could not reach the secure sign-in service. Check your connection and try again.";
    }

    if (error?.status === 503 || error?.code === "CORE_RUNTIME_UNAVAILABLE") {
        return "The secure sign-in service is temporarily unavailable. Please try again shortly.";
    }

    if (error?.status === 404) {
        return "The secure login API is not available on the current backend. Start the auth v2 backend service or point this app to the correct API.";
    }

    const codeMessage = getCodeMessage(error, OTP_ERROR_MESSAGES);
    if (codeMessage) {
        if (getAuthErrorCode(error) === "AUTH_OTP_RATE_LIMITED") {
            return getRateLimitMessage(error);
        }

        return codeMessage;
    }

    if (message.includes("expired")) {
        return "This verification code has expired. Request a new code and try again.";
    }

    if (message.includes("invalid") || message.includes("token")) {
        return "That verification code was not accepted. Check the code and try again, or request a new one.";
    }

    if (message.includes("rate limit") || message.includes("security purposes")) {
        return getRateLimitMessage(error);
    }

    return "We couldn't complete sign-in safely. Please request a new code and try again.";
}

export function getSignInErrorMessage(error) {
    if (isNetworkError(error)) {
        return "We could not reach the secure sign-in service. Check your connection and try again.";
    }

    if (error?.status === 503 || error?.code === "CORE_RUNTIME_UNAVAILABLE") {
        return "The secure sign-in service is temporarily unavailable. Please try again shortly.";
    }

    if (error?.status === 404) {
        return "The secure login API is not available on the current backend. Start the auth v2 backend service or point this app to the correct API.";
    }

    const codeMessage = getCodeMessage(error, SIGN_IN_ERROR_MESSAGES);
    if (codeMessage) {
        if (getAuthErrorCode(error) === "AUTH_OTP_RATE_LIMITED") {
            return getRateLimitMessage(error);
        }

        return codeMessage;
    }

    return "We couldn't confirm access for this sign-in attempt. Please try again or contact support if the problem continues.";
}
